import { T, useTranslate } from '@tolgee/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { MenuArrowDownSvg } from '../../../../../assets/icons';
import theme from '../../../../../assets/theme';
import { useOnOutsideClick } from '../../../../../hooks/useOnOutsideClick';
import { IconWrapper } from '../../../../_atoms/IconWrapper';
import { Grade } from '../../../../_molecules/SettingsModalDesigner/SettingsModalDesigner';

export type DropdownOption = {
  value: string;
  displayName: string | React.ReactNode;
  icon: React.ReactNode;
};

export function mapGradeToGradeWithOrdinalIndicator(
  grade: Grade,
): GradeTypeWithOrdinalIndicator {
  switch (grade) {
    case 'Pre-K':
      return 'Pre-K';
    case 'K':
      return 'Kindergarten';
    case '1':
      return '1st';
    case '2':
      return '2nd';
    case '3':
      return '3rd';
    case '4':
      return '4th';
    case '5':
      return '5th';
    case '6':
      return '6th';
    case '7':
      return '7th';
    case '8':
      return '8th';
    case '9':
      return '9th';
    case '10':
      return '10th';
    case '11':
      return '11th';
    case '12':
      return '12th';
    case 'Higher Ed':
      return 'Higher Education' as GradeTypeWithOrdinalIndicator;
    default:
      return grade;
  }
}

export const gradeOptions = [
  { displayName: 'Pre-K', value: 'Pre-K' },
  { displayName: 'K', value: 'Kindergarten' },
  { displayName: '1st', value: '1st' },
  { displayName: '2nd', value: '2nd' },
  { displayName: '3rd', value: '3rd' },
  { displayName: '4th', value: '4th' },
  { displayName: '5th', value: '5th' },
  { displayName: '6th', value: '6th' },
  { displayName: '7th', value: '7th' },
  { displayName: '8th', value: '8th' },
  { displayName: '9th', value: '9th' },
  { displayName: '10th', value: '10th' },
  { displayName: '11th', value: '11th' },
  { displayName: '12th', value: '12th' },
] as const;

export type GradeTypeWithOrdinalIndicator = typeof gradeOptions[number]['value'];

/**
 * Returns a translated grade display name for use in sentences.
 * @param grade - The grade value (e.g., "1", "K", "Pre-K")
 * @param t - The translation function from useTranslate hook
 * @returns The translated grade display name (e.g., "1st", "Kindergarten")
 */
export function useTranslatedGradeDisplayName(grade: Grade | undefined): string {
  const { t } = useTranslate();
  if (!grade) return '';

  const gradeWithOrdinal = mapGradeToGradeWithOrdinalIndicator(grade);
  const option = gradeOptions.find(opt => opt.value === gradeWithOrdinal);

  if (!option) return gradeWithOrdinal;
  switch (gradeWithOrdinal) {
    case 'Pre-K':
      return t('Pre-K', 'Pre-K');
    case 'Kindergarten':
      return t('Kindergarten', 'Kindergarten');
    case '1st':
      return t('1st', '1st');
    case '2nd':
      return t('2nd', '2nd');
    case '3rd':
      return t('3rd', '3rd');
    case '4th':
      return t('4th', '4th');
    case '5th':
      return t('5th', '5th');
    case '6th':
      return t('6th', '6th');
    case '7th':
      return t('7th', '7th');
    case '8th':
      return t('8th', '8th');
    case '9th':
      return t('9th', '9th');
    case '10th':
      return t('10th', '10th');
    case '11th':
      return t('11th', '11th');
    case '12th':
      return t('12th', '12th');
    default:
      assertNever(gradeWithOrdinal);
      return gradeWithOrdinal;
  }
}

function assertNever(value: never) {
  // eslint-disable-next-line no-console
  console.error('Unexpected grade:', value);
}

const loadLocalStorageGrade = () => {
  let grade;
  const gradeData = localStorage.getItem('@curipod_v1_ai_grade');
  if (gradeData) {
    try {
      const result = JSON.parse(gradeData) as { grade: string };
      grade = result.grade;
    } catch (e) {}
  }
  return grade;
};

export function GradeSelect({
  onSelect,
  value,
}: {
  value: GradeTypeWithOrdinalIndicator | undefined;
  onSelect: (value: GradeTypeWithOrdinalIndicator | undefined) => void;
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const { t } = useTranslate();
  const options = useMemo(
    () =>
      gradeOptions.map(option => ({
        ...option,
        displayName: t(option.displayName, option.displayName),
      })),
    [t],
  );

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (value: GradeTypeWithOrdinalIndicator) => {
    onSelect(value);
    setShowDropdown(false);
    localStorage.setItem('@curipod_v1_ai_grade', JSON.stringify({ grade: value }));
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const grade = loadLocalStorageGrade();
    if (grade && gradeOptions.some(o => o.value === grade) && !value) {
      onSelect(grade as GradeTypeWithOrdinalIndicator);
    }
  }, [onSelect]);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef}>
      <FakeInput onClick={() => setShowDropdown(!showDropdown)}>
        {selectedOption && <span style={{ flex: 1 }}>{selectedOption?.displayName}</span>}
        {!selectedOption && (
          <span style={{ flex: 1, color: theme.colors.iconGrey }}>
            <T keyName="Select Grade" defaultValue="Select Grade" />
          </span>
        )}
        <IconWrapper height="12px" width="12px">
          <MenuArrowDownSvg />
        </IconWrapper>
      </FakeInput>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: '100%',
                left: 0,
                background: 'white',
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map(option => (
                <OptionContainer
                  key={option.value}
                  $selected={option.value === value}
                  onClick={() => handleSelect(option.value)}
                >
                  <span>{option.displayName}</span>
                </OptionContainer>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
}

const OptionContainer = styled.div<{ $selected?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: ${theme.colors.greyLight};
  }
  user-select: none;

  ${({ $selected }) =>
    $selected &&
    css`
      background: #f6ece0;
      font-weight: bold;
    `}
`;

const FakeInput = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  border-radius: 5px;
  padding: 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  background: white;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  &:hover {
    box-shadow: ${theme.effects.floatingCreatorElementShadowHovered};
  }
`;

const Wrapper = styled.div<{ $fluid?: boolean }>`
  cursor: pointer;
  position: relative;
  width: 100%;
  height: 42px;
`;
